import { circle } from '@turf/circle'

function collectCandidateStrings(value, results = []) {
  if (typeof value === 'string') {
    results.push(value)
    return results
  }

  if (Array.isArray(value)) {
    value.forEach(item => collectCandidateStrings(item, results))
    return results
  }

  if (value && typeof value === 'object') {
    Object.values(value).forEach(item => collectCandidateStrings(item, results))
  }

  return results
}

function inferWorldCoverYear(gtif, tifInfo) {
  const candidateStrings = collectCandidateStrings([gtif, tifInfo])

  for (const candidate of candidateStrings) {
    const yearMatch = candidate.match(/(?:worldcover[^\d]*|[_-])(20\d{2})(?:[_-]|\b)/i)
    if (yearMatch) return yearMatch[1]
  }

  return null
}

function buildLandCoverAttribution(gtif, tifInfo) {
  const year = inferWorldCoverYear(gtif, tifInfo)
  const yearLabel = year ? ` ${year}` : ''

  return `&copy; <a href="https://esa-worldcover.org/" target="_blank" rel="noopener noreferrer">ESA WorldCover</a>${yearLabel}`
}

const rfCoverageLayers = [
  {
    id: 'rf-coverage-lte-m',
    data: '/data/rf-coverage/LTE-M_Coverage_Map_3GPP_4SC_GE_Barcelona_polygon.json',
  },
  {
    id: 'rf-coverage-5g-nr',
    data: '/data/rf-coverage/5G%20NR_Coverage_Map_3GPP_4SC_GDEM_Barcelona_polygon.json',
  },
]

function mapTrapFeatureToBuffer(feature, radius) {
  const { coordinates } = feature.geometry

  const circleFeature = circle(coordinates, radius, {
    units: 'meters',
    steps: 512,
  })

  return { ...circleFeature, properties: { ...feature.properties } }
}

function addLandCoverLayers(map, gtifs) {
  gtifs.forEach(async (gtif, index) => {
    const tifInfoUrl = `${import.meta.env.VITE_TITILER_URL}/cog/info?url=${encodeURIComponent(gtif)}`
    const tifInfo = await fetch(tifInfoUrl).then(r => r.json())
    const attribution = buildLandCoverAttribution(gtif, tifInfo)

    map.addSource(`land-cover-${index}`, {
      type: 'raster',
      tiles: [
        `${import.meta.env.VITE_TITILER_URL}/cog/tiles/WebMercatorQuad/{z}/{x}/{y}@2x.png?url=${encodeURIComponent(gtif)}`,
      ],
      tileSize: 512,
      bounds: tifInfo.bounds,
      attribution,
    })
    map.addLayer({
      id: `land-cover-layer-${index}`,
      type: 'raster',
      source: `land-cover-${index}`,
      paint: {
        'raster-opacity': 0.5,
      },
    }, 'environment-polygon-layer')
  })
}

function removeLandCoverLayers(map, gtifs) {
  gtifs.forEach((_, index) => {
    if (map.getLayer(`land-cover-layer-${index}`)) {
      map.removeLayer(`land-cover-layer-${index}`)
    }
    if (map.getSource(`land-cover-${index}`)) {
      map.removeSource(`land-cover-${index}`)
    }
  })
}

function addRfCoverageLayers(map, activeLayerIds = rfCoverageLayers.map(({ id }) => id)) {
  rfCoverageLayers.filter(({ id }) => activeLayerIds.includes(id)).forEach(({ id, data }) => {
    const fillLayerId = `${id}-fill`
    const lineLayerId = `${id}-line`

    if (!map.getSource(id)) {
      map.addSource(id, {
        type: 'geojson',
        data,
      })
    }

    if (!map.getLayer(fillLayerId)) {
      map.addLayer({
        id: fillLayerId,
        type: 'fill',
        source: id,
        paint: {
          'fill-pattern': 'nazka_diagonal-hatch',
          'fill-opacity': 1,
        },
      }, 'environment-polygon-layer')
    }

    if (!map.getLayer(lineLayerId)) {
      map.addLayer({
        id: lineLayerId,
        type: 'line',
        source: id,
        paint: {
          'line-color': '#000000',
          'line-width': 1,
          'line-opacity': 1,
        },
      }, 'environment-polygon-layer')
    }
  })
}

function removeRfCoverageLayers(map) {
  rfCoverageLayers.forEach(({ id }) => {
    const fillLayerId = `${id}-fill`
    const lineLayerId = `${id}-line`

    if (map.getLayer(lineLayerId)) {
      map.removeLayer(lineLayerId)
    }

    if (map.getLayer(fillLayerId)) {
      map.removeLayer(fillLayerId)
    }

    if (map.getSource(id)) {
      map.removeSource(id)
    }
  })
}

function convertBBox(latLonBBox) {
  const [latMin, lonMin, latMax, lonMax] = latLonBBox
  const bufferFactor = 0.5 // 50% expansion
  const latBuffer = (latMax - latMin) * bufferFactor
  const lonBuffer = (lonMax - lonMin) * bufferFactor
  const bbox = [lonMin - lonBuffer, latMin - latBuffer, lonMax + lonBuffer, latMax + latBuffer]
  return bbox
}

export { mapTrapFeatureToBuffer, addLandCoverLayers, removeLandCoverLayers, addRfCoverageLayers, removeRfCoverageLayers, convertBBox }
